import { CreativeOverviewStateContent } from './CreativeOverviewStateContent';
import { CreativeOverviewStateContentModel } from './CreativeOverviewStateContentModel';
import { BindCreativeStateContent } from './BindCreativeStateContent';
import { CreativeSummaryStateContent } from './CreativeSummaryStateContent';
import i18n from 'i18next';
import { ModalState } from 'containers/Common/ModalStateFactory';
import { BindCreativeStateContentModel } from './BindCreativeStateContentModel';
import { CreativeSummaryStateContentModel } from './CreativeSummaryStateContentModel';
import { ModifyCreativeActivationStateContentModel } from './ModifyCreativeActivationStateContentModel';
import { ModifyCreativeActivationStateContent } from './ModifyCreativeActivationStateContent';
import { CreativeBindData } from 'core/rtbCampaign/CreativeBindData';
import { renderBatchEditCampaignTitle } from 'components/BatchEditCampaign/BatchEditCampaignTitle';

/**
 * 「這幾檔活動的機台名額滿了」的提示。
 *
 * 綁定與啟用兩個畫面共用一支：條件一樣、處理方式也一樣（回列表拿掉那幾檔，或去編輯活動
 * 移掉超限的版位），各寫一次就會出現兩種講法。不沿用活動列表那句 epaperNotActivatableHint
 * 是因為那句的主詞與動作都是「所選活動 …… 再啟用」，放到綁定這邊會對不上使用者按的鍵。
 */
function epaperSpaceQuotaFullTip(campaignNames: string[]): string {
  return i18n.t(
    'creativeOverviewStateContent.buttons.epaperSpaceQuotaFullTipsWithCampaigns',
    { names: campaignNames.join(', ') }
  );
}

abstract class BasicCreativeManagementState {
  campaigns: Array<CreativeBindData>;
  back?;
  next?;
  close?;

  constructor(campaigns: Array<CreativeBindData>) {
    this.campaigns = campaigns;
  }

  get renderTitle() {
    return () => renderBatchEditCampaignTitle(this.campaigns);
  }

  setBackFn = (back) => {
    this.back = back;
  };

  setNextFn = (next) => {
    this.next = next;
  };

  setCloseFn = (close) => {
    this.close = close;
  };

  canGoNext = () => {
    return true;
  };
}

export class CreativeOverviewState
  extends BasicCreativeManagementState
  implements ModalState
{
  constructor(
    campaigns: Array<CreativeBindData>,
    public contentModel: CreativeOverviewStateContentModel
  ) {
    super(campaigns);
  }

  get content() {
    return CreativeOverviewStateContent;
  }

  get btnData(): any {
    return {
      primaryButton: this.close && {
        title: i18n.t('common.buttons.close'),
        callback: () => {
          this.close(this.contentModel.state.dirty);
        }
      }
    };
  }
}

export class BindCreativeState
  extends BasicCreativeManagementState
  implements ModalState
{
  constructor(
    campaigns: Array<CreativeBindData>,
    public contentModel: BindCreativeStateContentModel
  ) {
    super(campaigns);
  }

  get content() {
    return BindCreativeStateContent;
  }

  get btnData() {
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.goSummary'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.cancel'),
        callback: this.back
      },
      dangerButton: this.close && {
        title: i18n.t('common.buttons.close'),
        variant: 'secondary',
        callback: () => {
          this.close(false);
        }
      }
    };
  }

  canGoNext = () => {
    const selectedCount = this.contentModel.selectedCreatives.length;
    if (selectedCount === 0) {
      return false;
    }
    // ECOPD-5640：名額滿的活動不分渠道限制、也不分選幾份，一律擋掉。
    if (this.contentModel.quotaFullCampaignNames.length > 0) {
      return false;
    }
    if (!this.contentModel.onlyOneCreative) {
      return true;
    }
    return selectedCount === 1 && !this.contentModel.hasActiveCreative;
  };

  /**
   * 兩種擋法要給不一樣的話：「活動已經有啟用中的素材」是選幾份都過不了，「選太多」是
   * 少選幾份就能過。一個都沒選時不給提示——按鈕本來就是灰的，那是還沒開始選，不是被擋住。
   *
   * 已有啟用中素材時借用 creativeOverviewStateContent 的 singleActiveBindTips：文案講的
   * 就是這件事，另開一個 key 只會讓同一句話在 i18n sheet 出現兩份。
   */
  get nextBlockedTip() {
    // 名額滿是「少選幾份也過不了」，而且與渠道的單一素材限制無關，所以排在最前面。
    const quotaFullNames = this.contentModel.quotaFullCampaignNames;
    if (quotaFullNames.length > 0) {
      return epaperSpaceQuotaFullTip(quotaFullNames);
    }
    if (!this.contentModel.onlyOneCreative) {
      return undefined;
    }
    if (this.contentModel.hasActiveCreative) {
      return i18n.t(
        'creativeOverviewStateContent.buttons.singleActiveBindTips'
      );
    }
    return this.contentModel.selectedCreatives.length > 1
      ? i18n.t('bindCreativeStateContent.labels.singleCreativeTips')
      : undefined;
  }
}

export class ModifyCreativeActivationState
  extends BasicCreativeManagementState
  implements ModalState
{
  constructor(
    campaigns: Array<CreativeBindData>,
    public contentModel: ModifyCreativeActivationStateContentModel
  ) {
    super(campaigns);
  }

  get content() {
    return ModifyCreativeActivationStateContent;
  }

  get btnData() {
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.goSummary'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.cancel'),
        callback: this.back
      },
      dangerButton: this.close && {
        title: i18n.t('common.buttons.close'),
        variant: 'secondary',
        callback: () => {
          this.close(false);
        }
      }
    };
  }

  canGoNext = () => {
    return (
      this.contentModel.selectedCreatives.length > 0 &&
      !this.contentModel.exceedsSingleActiveLimit &&
      this.contentModel.quotaFullCampaignNames.length === 0
    );
  };

  get nextBlockedTip() {
    // ECOPD-5640：名額滿排在前面 —— 它是「這檔活動根本排不進機台」，少選幾份也沒用；
    // 兩份啟用中則是少選幾份就能過。
    const quotaFullNames = this.contentModel.quotaFullCampaignNames;
    if (quotaFullNames.length > 0) {
      return epaperSpaceQuotaFullTip(quotaFullNames);
    }
    return this.contentModel.exceedsSingleActiveLimit
      ? i18n.t('creativeOverviewStateContent.buttons.singleActiveActivateTips')
      : undefined;
  }
}

export class CreativeSummaryState
  extends BasicCreativeManagementState
  implements ModalState
{
  constructor(
    campaigns: Array<CreativeBindData>,
    public contentModel: CreativeSummaryStateContentModel
  ) {
    super(campaigns);
    this.next = async () => {
      await this.contentModel.save();
      this.close && this.close(true);
    };
  }

  get content() {
    return CreativeSummaryStateContent;
  }

  get btnData() {
    return {
      primaryButton: this.next && {
        title: i18n.t('common.buttons.ok'),
        callback: this.next
      },
      secondaryButton: this.back && {
        title: i18n.t('common.buttons.backModify'),
        callback: this.back
      },
      dangerButton: this.close && {
        title: i18n.t('common.buttons.cancel'),
        callback: () => {
          this.close(false);
        },
        variant: 'secondary'
      }
    };
  }
}
